// Knowledge base spaces — ontwerp §3.2/§3.3, §6.1 (`:space`) and §7.1
// (agent paths).
//
// Two notations:
// - REST / web / MCP: `workspace` or `project:<projectId>` (the `:space`
//   route segment and the `space` field on every DTO).
// - Agents (run-tools): one flat namespace with the prefixes `project/`
//   (the run's own project) and `workspace/`. An agent can never name
//   another project — the run decides which project `project/` means.

import { z } from 'zod'
import { kbFolderError, kbPathError, normalizeKbFolder, normalizeKbPath } from './path.js'

export type KbSpace = 'workspace' | `project:${string}`

export type KbSpaceRef = { kind: 'workspace' } | { kind: 'project'; projectId: string }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `workspace` → `{kind:'workspace'}`, `project:<uuid>` → `{kind:'project', projectId}`, else `null`. */
export function parseKbSpace(value: string): KbSpaceRef | null {
  if (value === 'workspace') return { kind: 'workspace' }
  if (value.startsWith('project:')) {
    const projectId = value.slice('project:'.length).toLowerCase()
    return UUID_RE.test(projectId) ? { kind: 'project', projectId } : null
  }
  return null
}

export function formatKbSpace(ref: KbSpaceRef): KbSpace {
  return ref.kind === 'workspace' ? 'workspace' : `project:${ref.projectId}`
}

/** The space of a row: `project_id` null → workspace. */
export function kbSpaceOf(projectId: string | null | undefined): KbSpace {
  return projectId ? `project:${projectId}` : 'workspace'
}

/** The project id a space addresses, or `null` for the workspace space. */
export function kbSpaceProjectId(space: KbSpace | KbSpaceRef): string | null {
  const ref = typeof space === 'string' ? parseKbSpace(space) : space
  return ref && ref.kind === 'project' ? ref.projectId : null
}

export const KbSpaceSchema = z
  .string()
  .refine((value) => parseKbSpace(value) !== null, 'Use "workspace" or "project:<projectId>"')
  .transform((value) => formatKbSpace(parseKbSpace(value)!))

// ---------------------------------------------------------------------------
// Agent notation (`project/…`, `workspace/…`)
// ---------------------------------------------------------------------------

export const KB_AGENT_SPACES = ['project', 'workspace'] as const
export type KbAgentSpace = (typeof KB_AGENT_SPACES)[number]

export interface KbAgentPath {
  space: KbAgentSpace
  /** Path relative to the space, normalized and validated. */
  path: string
}

/**
 * Parses an agent path such as `project/areas/api.md`. Throws an Error
 * with a message an agent can act on.
 */
export function parseKbAgentPath(input: string): KbAgentPath {
  const normalized = normalizeKbPath(input)
  const slash = normalized.indexOf('/')
  const head = slash === -1 ? normalized : normalized.slice(0, slash)
  if (head !== 'project' && head !== 'workspace') {
    throw new Error(`Knowledge base paths start with "project/" or "workspace/" (got "${input}")`)
  }
  const path = slash === -1 ? '' : normalized.slice(slash + 1)
  const error = kbPathError(path)
  if (error) throw new Error(`Invalid knowledge base path "${input}": ${error}`)
  return { space: head, path }
}

export interface KbAgentFolder {
  /** `null` = both spaces (the agent passed no folder, or ''). */
  space: KbAgentSpace | null
  /** '' or 'a/b/' within that space. */
  folder: string
}

/** Parses an optional agent folder: '' → both spaces; 'project' / 'project/areas' → that space + prefix. */
export function parseKbAgentFolder(input: string | undefined | null): KbAgentFolder {
  const normalized = normalizeKbFolder(input ?? '')
  if (normalized === '') return { space: null, folder: '' }
  const slash = normalized.indexOf('/')
  const head = normalized.slice(0, slash)
  if (head !== 'project' && head !== 'workspace') {
    throw new Error(`Knowledge base folders start with "project/" or "workspace/" (got "${input}")`)
  }
  const folder = normalized.slice(slash + 1)
  const error = kbFolderError(folder)
  if (error) throw new Error(`Invalid knowledge base folder "${input}": ${error}`)
  return { space: head, folder }
}

export function formatKbAgentPath(space: KbAgentSpace, path: string): string {
  return `${space}/${path}`
}

/** Agent prefix for a row: a page in a project space is always the run's own project. */
export function kbAgentSpaceOf(projectId: string | null | undefined): KbAgentSpace {
  return projectId ? 'project' : 'workspace'
}
